import { CANONICAL_ORIGIN, listingCanonicalUrl } from './canonical';
import { toEmbedUrl } from '@/src/lib/utils/videoUrls';
import { type AmenityHighlight, getListingPageDescription, getPublicListingName } from './listing-metadata';
import { hasBookableTourProvider, type InstantTourProvidersLike } from '@/src/lib/utils/instantTourProviders';
import { BOOK_TOUR_HASH } from '@/src/lib/constants';

type Breadcrumb = { label: string; url?: string; href?: string };

type ListingJsonLdAddress = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  hideStreet?: boolean;
};

export type ListingJsonLdPhoto = { url?: string; caption?: string };

export type ListingFees = { moveIn?: FeeSectionLike; monthly?: FeeSectionLike };

/** Structural subset of CostsAndFees; `amount` is a union of text or numbers. */
type FeeSectionLike = {
  feeGroup?: Array<{
    fees?: Array<{
      displayName?: string;
      amount?: { displayText?: string } | { min?: number; max?: number | null };
    }>;
  }>;
};

type ListingJsonLdCoordinates = { lat?: number; lon?: number };

export type ListingJsonLdInventory = {
  totalUnitCount?: number;
  availableUnitCount?: number;
};

/**
 * Structural subset of PropertyDetailListing needed to emit listing schema.
 * All fields are optional — HDP listing payloads are Partial<ListingDetails>.
 */
export type ListingJsonLdListing = {
  active?: boolean;
  isAllowedToRetain?: boolean;
  created?: number;
  floorplans?: Array<{ availabilityDate?: string }>;
  listingVideos?: Array<{ url?: string; label?: string | null }>;
  displayName?: string;
  title?: string;
  propertyType?: string;
  building?: boolean;
  isApartmentBldg?: boolean;
  trusted?: boolean; // selects the legacy description copy in getListingPageDescription
  address?: ListingJsonLdAddress;
  geo?: ListingJsonLdCoordinates;
  largePhotos?: Array<ListingJsonLdPhoto>;
  photos?: Array<ListingJsonLdPhoto>;
  previewPhoto?: ListingJsonLdPhoto;
  medPhotoUrls?: Array<string>;
  listingMinMaxPriceBeds?: {
    minPrice?: number;
    maxPrice?: number;
    minBeds?: number;
    maxBeds?: number;
    minBaths?: number;
    maxBaths?: number;
    minSqft?: number;
    maxSqft?: number;
  };
  modelsAndPricing?: Array<{ numBeds?: number; lowPrice?: number; highPrice?: number; quantity?: number }>;
  modelTypeUnitCount?: number;
  listedBy?: {
    companyName?: string;
    contactName?: string;
    contactPhone?: string;
    logoUrlCdn?: string | null;
  };
  amenities?: {
    amenities?: Array<string>;
    highlights?: Array<AmenityHighlight>;
    condensedAmenities?: { pets?: string | null };
  };
  details?: {
    numUnits?: number; // building detection in getListingPageDescription
    fullDescription?: string;
    webSiteOriginalDomain?: string;
    leaseTerms?: string;
    availabilityDate?: string;
    tourUrls?: Array<string>; // 3DModel.contentUrl, one node per url
    tourLabels?: Array<string>; // 3DModel.name
    instantTourProviders?: InstantTourProvidersLike | null; // presence gates tourBookingPage
  };
};

function cleanString(value: string | undefined): string | undefined {
  const cleaned = value?.trim();
  return cleaned || undefined;
}

function includesStreetText(value: string, street: string): boolean {
  const normalize = (text: string) => text.toLowerCase().replace(/\s+/g, ' ').trim();
  return normalize(value).includes(normalize(street));
}

/**
 * Returns a listing name that is safe to publish. Listings with an undisclosed
 * address must not fall back to, or use a title that contains, the street.
 */
export function publicListingName(listing: Pick<ListingJsonLdListing, 'address' | 'displayName' | 'title'>) {
  const street = cleanString(listing.address?.street);
  const hideStreet = listing.address?.hideStreet === true;

  for (const value of [listing.displayName, listing.title]) {
    const candidate = cleanString(value);
    if (candidate && (!hideStreet || !street || !includesStreetText(candidate, street))) {
      return candidate;
    }
  }

  return hideStreet ? undefined : street;
}

function validGeoCoordinates(coordinates: ListingJsonLdCoordinates | undefined): { lat: number; lon: number } | undefined {
  const lat = coordinates?.lat;
  const lon = coordinates?.lon;

  if (
    typeof lat !== 'number' ||
    typeof lon !== 'number' ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lon) ||
    lat < -90 ||
    lat > 90 ||
    lon < -180 ||
    lon > 180 ||
    (lat === 0 && lon === 0)
  ) {
    return undefined;
  }

  return { lat, lon };
}

/** Escapes JSON-LD for safe insertion into an inline script element. */
export function serializeJsonLd(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

const PROPERTY_TYPE_TO_SCHEMA_TYPE: Record<string, string> = {
  house: 'House',
  townhouse: 'House',
  condo: 'Apartment',
  divided: 'Apartment',
  medium: 'ApartmentComplex',
  large: 'ApartmentComplex',
  garden: 'ApartmentComplex',
  land: 'Residence',
};

function listingSchemaType(listing: ListingJsonLdListing, isBuildingPage: boolean): string {
  if (isBuildingPage) return 'ApartmentComplex';
  const propertyType = listing.propertyType?.toLowerCase();
  if (propertyType) return PROPERTY_TYPE_TO_SCHEMA_TYPE[propertyType] || 'Apartment';
  return listing.building || listing.isApartmentBldg ? 'ApartmentComplex' : 'Apartment';
}

function listingPostalAddress(address: ListingJsonLdAddress | undefined) {
  if (!address) return undefined;
  return {
    '@type': 'PostalAddress',
    ...(address.street && !address.hideStreet ? { streetAddress: address.street } : {}),
    ...(address.city ? { addressLocality: address.city } : {}),
    ...(address.state ? { addressRegion: address.state } : {}),
    ...(address.zip ? { postalCode: address.zip } : {}),
    addressCountry: 'US',
  };
}

/**
 * Photo precedence shared by listing JSON-LD and HDP OG/Twitter metadata:
 * largePhotos → photos → previewPhoto → medPhotoUrls.
 */
export function listingImages(listing: ListingJsonLdListing): Array<string> {
  const photos = listing.largePhotos?.length ? listing.largePhotos : listing.photos;
  const urls = photos?.map((photo) => photo.url).filter((url): url is string => Boolean(url)) ?? [];
  if (urls.length > 0) return urls.slice(0, 5);
  if (listing.previewPhoto?.url) return [listing.previewPhoto.url];
  return listing.medPhotoUrls?.slice(0, 5) ?? [];
}

/**
 * The first photo selected by the shared listing-image precedence. Keeping the
 * metadata preview aligned with JSON-LD prevents social cards from describing
 * a different image than search engines index.
 */
export function listingPrimaryImage(listing: ListingJsonLdListing): ListingJsonLdPhoto | undefined {
  const photos = listing.largePhotos?.length ? listing.largePhotos : listing.photos;
  if (photos?.length) return photos.find((photo) => Boolean(photo.url));
  if (listing.previewPhoto?.url) return listing.previewPhoto;
  const url = listing.medPhotoUrls?.[0];
  return url ? { url } : undefined;
}

function listingPriceRange(listing: ListingJsonLdListing): { low: number; high: number } | null {
  const minMax = listing.listingMinMaxPriceBeds;
  const minPrice = validPrice(minMax?.minPrice);
  if (minPrice !== undefined) {
    return { low: minPrice, high: Math.max(validPrice(minMax?.maxPrice) ?? minPrice, minPrice) };
  }
  const modelPrices = (listing.modelsAndPricing ?? [])
    .flatMap((model) => [model.lowPrice, model.highPrice])
    .map(validPrice)
    .filter((price): price is number => price !== undefined);
  if (modelPrices.length === 0) return null;
  return { low: Math.min(...modelPrices), high: Math.max(...modelPrices) };
}

function validPrice(value: number | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * One 3DModel node per tour URL (`details.tourUrls`/`tourLabels` are the v3
 * adapter's pre-deduped parallel arrays). 3D tours are deliberately NOT the
 * legacy virtual-Event markup, which Google's event content guidelines now
 * disallow (HP-8483).
 */
function listingTour3dModels(listing: ListingJsonLdListing) {
  return tour3dModelNodes(listing.details?.tourUrls ?? [], listing.details?.tourLabels ?? []);
}

function tour3dModelNodes(urls: Array<string | undefined>, labels: Array<string | undefined>) {
  return urls.flatMap((rawUrl, index) => {
    const url = cleanString(rawUrl);
    if (!url) return [];
    return [{ '@type': '3DModel', name: cleanString(labels[index]) ?? '3D tour', contentUrl: url }];
  });
}

function knownUnitCount(value: number | undefined, allowZero: boolean): number | undefined {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) return undefined;
  return value > 0 || (allowZero && value === 0) ? value : undefined;
}

function listingAmenityFeatures(listing: ListingJsonLdListing) {
  return (listing.amenities?.amenities ?? [])
    .map(cleanString)
    .filter((name): name is string => Boolean(name))
    .map((name) => ({ '@type': 'LocationFeatureSpecification', name }));
}

/**
 * An expired listing that may not be retained must not publish its description;
 * the visible modules already hide it (`canRetainData` in the module registry).
 */
function canPublishRetainedData(listing: ListingJsonLdListing): boolean {
  return listing.active !== false || listing.isAllowedToRetain !== false;
}

/** The one trimmed manager number every node publishes, so two nodes cannot disagree. */
function listingPhone(listing: ListingJsonLdListing): string | undefined {
  return cleanString(listing.listedBy?.contactPhone);
}

/**
 * The v3 adapter runs every missing timestamp through `numberOrZero`, so an
 * absent date arrives as 0 rather than undefined. A `!= null` guard would
 * publish 1970-01-01.
 */
function isoDate(epochMs: number | undefined): string | undefined {
  if (typeof epochMs !== 'number' || !Number.isFinite(epochMs) || epochMs <= 0) return undefined;
  const date = new Date(epochMs);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/** The newest listing update among the given listings, in epoch ms; undefined when none has one. */
export function newestListingUpdateMs(listings: Array<{ updated?: number }>): number | undefined {
  const newest = Math.max(0, ...listings.map((listing) => listing.updated ?? 0));
  return newest > 0 ? newest : undefined;
}

/**
 * Move-in date. `details.availabilityDate` is hard-coded empty by the v3
 * adapter, so the real value lives on the first floorplan — the same source
 * `getListingAvailabilityDisplay` reads for the visible page.
 */
function availabilityStarts(listing: ListingJsonLdListing): string | undefined {
  const date = cleanString(listing.floorplans?.[0]?.availabilityDate) ?? cleanString(listing.details?.availabilityDate);
  return date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : undefined;
}

/**
 * `details.leaseTerms` is free text. schema.org wants a Duration, so publish
 * only what parses to one and omit the rest rather than emit an invalid value.
 */
function leaseLength(leaseTerms: string | undefined): { '@type': 'QuantitativeValue'; value: number; unitCode: 'MON' } | undefined {
  const match = cleanString(leaseTerms)?.match(/(\d{1,2})\s*(?:\+\s*)?month/i);
  const months = match ? Number(match[1]) : undefined;
  return months && months > 0 ? { '@type': 'QuantitativeValue', value: months, unitCode: 'MON' } : undefined;
}

/** How many offers the AggregateOffer stands for. */
function offerCount(listing: ListingJsonLdListing, availableUnitCount: number | undefined): number | undefined {
  if (availableUnitCount !== undefined && availableUnitCount > 0) return availableUnitCount;
  const models = listing.modelsAndPricing ?? [];
  const quantity = models.reduce((total, model) => total + (model.quantity ?? 0), 0);
  if (quantity > 0) return quantity;
  if (models.length > 0) return models.length;
  return knownUnitCount(listing.modelTypeUnitCount, false);
}

/**
 * Images as ImageObject rather than bare URLs, carrying a caption where the
 * source photo has one. The v3 adapter sets every caption to '' on unit HDPs,
 * so in practice only the legacy building path supplies them.
 */
function listingImageObjects(listing: ListingJsonLdListing) {
  const photos = listing.largePhotos?.length ? listing.largePhotos : listing.photos;
  const captioned = (photos ?? []).filter((photo) => photo.url).slice(0, 5);
  if (captioned.length > 0) {
    return captioned.map((photo) => ({
      '@type': 'ImageObject',
      contentUrl: photo.url as string,
      ...(cleanString(photo.caption) ? { caption: cleanString(photo.caption) } : {}),
    }));
  }
  return listingImages(listing).map((url) => ({ '@type': 'ImageObject', contentUrl: url }));
}

/**
 * The named party behind the listing — the E-E-A-T signal the graph has none of.
 * `listedBy.webSite` is a HotPads click-tracking redirect, so the real
 * destination comes from `details.webSiteOriginalDomain` instead.
 */
function listingProvider(listing: ListingJsonLdListing) {
  const listedBy = listing.listedBy;
  const street = cleanString(listing.address?.street);
  const rawName = cleanString(listedBy?.companyName) ?? cleanString(listedBy?.contactName);
  // PropertyManagerModule suppresses a name that is just the street address.
  const name = rawName && (!street || !includesStreetText(rawName, street)) ? rawName : undefined;
  if (!name) return undefined;

  const url = cleanString(listing.details?.webSiteOriginalDomain);
  const logo = cleanString(listedBy?.logoUrlCdn ?? undefined);
  const phone = listingPhone(listing);
  return {
    '@type': 'RealEstateAgent',
    name,
    ...(url ? { url } : {}),
    ...(phone ? { telephone: phone } : {}),
    ...(logo ? { logo: { '@type': 'ImageObject', contentUrl: logo } } : {}),
  };
}

/**
 * Fees as priceSpecification. `Amount` is a union of a preformatted
 * `displayText` or a numeric `{min,max}`; only the numeric form can be
 * published, so a text-only fee is omitted rather than guessed at.
 * `details.feesAndDeposits` is hard-coded empty by the v3 adapter and unread by
 * the app. The live `costsAndFees` comes from its own upstream call inside a
 * streamed module, so it is passed in rather than read off the listing, and the
 * node it produces merges into the offer by `@id`.
 */
export function feePriceSpecifications(fees: ListingFees | undefined) {
  const sections: Array<[FeeSectionLike | undefined, 'monthly' | 'oneTime']> = [
    [fees?.monthly, 'monthly'],
    [fees?.moveIn, 'oneTime'],
  ];

  return sections.flatMap(([section, kind]) =>
    (section?.feeGroup ?? []).flatMap((group) =>
      (group.fees ?? []).flatMap((fee) => {
        const amount = fee.amount;
        const min = amount && 'min' in amount ? validPrice(amount.min) : undefined;
        const name = cleanString(fee.displayName);
        if (min === undefined || !name) return [];
        const max = amount && 'max' in amount ? validPrice(amount.max ?? undefined) : undefined;
        return [
          {
            '@type': kind === 'monthly' ? 'UnitPriceSpecification' : 'PriceSpecification',
            name,
            price: min,
            priceCurrency: 'USD',
            ...(max !== undefined && max > min ? { maxPrice: max } : {}),
            ...(kind === 'monthly' ? { unitCode: 'MON' } : {}),
          },
        ];
      }),
    ),
  );
}

/**
 * Fees merge into the offer node by `@id`, from the streamed module that
 * already fetched them. Returns undefined when no fee resolves to a number, so
 * the page never carries an empty node.
 */
export function buildOfferFeesJsonLd(listingUrl: string, fees: ListingFees | undefined) {
  const priceSpecification = feePriceSpecifications(fees);
  if (priceSpecification.length === 0) return undefined;

  return {
    '@context': 'https://schema.org',
    '@id': offerNodeId(listingUrl),
    '@type': 'AggregateOffer',
    priceSpecification,
  };
}

/**
 * VideoObject per listing video. The payload carries only a url and an optional
 * label (HP-9035), so two required properties are deliberate approximations:
 * `thumbnailUrl` is the listing's hero image — the same one already published as
 * the primary image — and `uploadDate` is the listing's creation date, which is
 * generally earlier than the true upload and so understates freshness rather
 * than inflating it. Both are documented in /llms.txt.
 *
 * Emits nothing unless every property Google requires resolves, so a thin video
 * never produces a partial node.
 */
function listingVideoNodes(
  listing: ListingJsonLdListing,
  url: string,
  name: string | undefined,
  description: string | undefined,
  thumbnailUrl: string | undefined,
  uploadDate: string | undefined,
) {
  if (!thumbnailUrl || !uploadDate || !description) return [];

  return (listing.listingVideos ?? []).flatMap((video, index) => {
    const contentUrl = cleanString(video.url);
    if (!contentUrl) return [];
    const embedUrl = toEmbedUrl(contentUrl);
    return [
      {
        '@type': 'VideoObject',
        '@id': `${url}#video-${index + 1}`,
        name: cleanString(video.label ?? undefined) ?? `${name ?? 'Rental'} video tour`,
        description,
        thumbnailUrl,
        uploadDate,
        contentUrl,
        ...(embedUrl !== contentUrl ? { embedUrl } : {}),
      },
    ];
  });
}

/**
 * An expired listing keeps rendering its HDP as historic property details, so
 * the offer must say so rather than advertise the last known rent as live.
 * `getHdpFaqQuestions` already gates on the same `active === false`.
 */
function offerAvailability(
  isActive: boolean,
  isBuilding: boolean,
  availableUnitCount: number | undefined,
): string | undefined {
  if (!isActive) return 'https://schema.org/OutOfStock';
  if (!isBuilding) return 'https://schema.org/InStock';
  if (availableUnitCount === undefined) return undefined;
  return availableUnitCount > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
}

/**
 * The `#residence` identity for a listing, derived from its canonical URL.
 * Shared so the HDP node and the SRP ItemList entry cannot drift apart.
 */
export function listingNodeId(canonicalUrlForListing: string): string {
  return `${canonicalUrlForListing}#residence`;
}

/**
 * Listing schema for HDP pages: an Accommodation-family node (Apartment /
 * House / ApartmentComplex) with address + GeoCoordinates, plus a Product
 * node carrying the rental Offer so price surfaces in rich results.
 *
 * `fallbackGeo` covers HDP payloads whose coordinates live beside the listing
 * (the fetcher's top-level `geo`) rather than on it.
 */
export function buildListingNodes(
  listing: ListingJsonLdListing,
  url: string,
  fallbackGeo?: ListingJsonLdCoordinates,
  inventory?: ListingJsonLdInventory,
  isBuildingPage = false,
  units?: Array<unknown>, // the meta description counts these; pass the same list so the two agree
): Array<GraphNode> {
  const name = getPublicListingName(listing);
  const address = listingPostalAddress(listing.address);
  const images = listingImages(listing);
  const tour3dModels = listingTour3dModels(listing);
  const geoSource = validGeoCoordinates(listing.geo) ?? validGeoCoordinates(fallbackGeo);
  const geo = geoSource
    ? { '@type': 'GeoCoordinates', latitude: geoSource.lat, longitude: geoSource.lon }
    : undefined;
  const minMax = listing.listingMinMaxPriceBeds;
  const price = listingPriceRange(listing);
  const schemaType = listingSchemaType(listing, isBuildingPage);
  const isBuilding = schemaType === 'ApartmentComplex';
  const totalUnitCount = knownUnitCount(inventory?.totalUnitCount, false);
  const availableUnitCount = knownUnitCount(inventory?.availableUnitCount, true);
  const availability = offerAvailability(listing.active !== false, isBuilding, availableUnitCount);
  const amenityFeature = listingAmenityFeatures(listing);
  const offers = offerCount(listing, availableUnitCount);
  // tourBookingPage is only defined on Accommodation subtypes + ApartmentComplex,
  // not on bare Residence (the land mapping). Tours are booked on the HDP itself.
  const tourBookingPage =
    schemaType !== 'Residence' && hasBookableTourProvider(listing.details?.instantTourProviders)
      ? `${url}#${BOOK_TOUR_HASH}`
      : undefined;

  const description = canPublishRetainedData(listing) ? cleanString(listing.details?.fullDescription) : undefined;
  const petsAllowed = cleanString(listing.amenities?.condensedAmenities?.pets ?? undefined);
  const datePosted = isoDate(listing.created);
  const availability_ = availabilityStarts(listing);
  const lease = leaseLength(listing.details?.leaseTerms);
  const imageObjects = listingImageObjects(listing);
  const videoNodes = listingVideoNodes(
    listing,
    url,
    name,
    description,
    listingPrimaryImage(listing)?.url,
    datePosted,
  );
  const provider = listingProvider(listing);
  // Read directly: listingProvider drops a street-only company name, and the phone must survive.
  // Like the description, an expired listing that may not be retained must not publish it.
  const managerPhone = canPublishRetainedData(listing) ? listingPhone(listing) : undefined;

  const residence = {
    '@type': schemaType,
    '@id': listingNodeId(url),
    name,
    url,
    ...(imageObjects.length > 0 ? { photo: imageObjects } : {}),
    ...(description ? { description } : {}),
    ...(petsAllowed ? { petsAllowed } : {}),
    ...(datePosted ? { datePosted } : {}),
    ...(availability_ ? { availabilityStarts: availability_ } : {}),
    ...(lease ? { leaseLength: lease } : {}),
    ...(images.length > 0 ? { image: images } : {}),
    ...(address ? { address } : {}),
    ...(geo ? { geo } : {}),
    ...(isBuilding && managerPhone ? { telephone: managerPhone } : {}),
    ...(totalUnitCount !== undefined ? { numberOfAccommodationUnits: totalUnitCount } : {}),
    ...(availableUnitCount !== undefined ? { numberOfAvailableAccommodationUnits: availableUnitCount } : {}),
    ...(minMax?.minBeds != null ? { numberOfBedrooms: minMax.minBeds } : {}),
    // numberOfBathroomsTotal and floorSize are Accommodation-only properties;
    // schema.org does not define them on ApartmentComplex.
    ...(!isBuilding && minMax?.minBaths ? { numberOfBathroomsTotal: minMax.minBaths } : {}),
    ...(!isBuilding && minMax?.minSqft
      ? { floorSize: { '@type': 'QuantitativeValue', value: minMax.minSqft, unitCode: 'FTK' } }
      : {}),
    ...(amenityFeature.length > 0 ? { amenityFeature } : {}),
    ...(tour3dModels.length > 0 || videoNodes.length > 0
      ? { subjectOf: [...tour3dModels, ...videoNodes.map((video) => ({ '@id': video['@id'] }))] }
      : {}),
    ...(tourBookingPage ? { tourBookingPage } : {}),
  };

  const product = price
    ? {
        '@type': 'Product',
        '@id': `${url}#product`,
        name,
        url,
        description: getListingPageDescription({ listing, units, isBuildingPage }),
        ...(images.length > 0 ? { image: images } : {}),
        offers: {
          '@type': 'AggregateOffer',
          '@id': offerNodeId(url),
          priceCurrency: 'USD',
          lowPrice: price.low,
          highPrice: price.high,
          ...(availability ? { availability } : {}),
          ...(offers !== undefined ? { offerCount: offers } : {}),
          ...(provider ? { provider } : {}),
          itemOffered: { '@id': listingNodeId(url) },
          url,
        },
      }
    : null;

  return [residence, ...(product ? [product] : []), ...videoNodes];
}

/** Standalone listing graph. Prefer `buildPageGraphJsonLd` with `buildListingNodes`. */
export function buildListingJsonLd(
  listing: ListingJsonLdListing,
  url: string,
  fallbackGeo?: ListingJsonLdCoordinates,
  inventory?: ListingJsonLdInventory,
  isBuildingPage = false,
  units?: Array<unknown>,
) {
  return {
    '@context': 'https://schema.org',
    '@graph': buildListingNodes(listing, url, fallbackGeo, inventory, isBuildingPage, units),
  };
}

type GraphNode = Record<string, unknown>;

/** A standalone builder's output embeds as a graph node once `@context` is dropped. */
function asGraphNode(node: GraphNode): GraphNode {
  const { '@context': _context, ...rest } = node;
  return rest;
}

/** The `@id` of the site-wide WebSite node every page graph hangs from. */
export const WEBSITE_NODE_ID = `${CANONICAL_ORIGIN}/#website`;

export function pageNodeId(url: string): string {
  return `${url}#webpage`;
}

export function breadcrumbNodeId(url: string): string {
  return `${url}#breadcrumb`;
}

export function faqNodeId(url: string): string {
  return `${url}#faq`;
}

export function itemListNodeId(url: string): string {
  return `${url}#listings`;
}

export function offerNodeId(url: string): string {
  return `${url}#offer`;
}

/**
 * One linked graph per page. Without a page node the listing, breadcrumb and
 * ItemList arrive as unrelated facts about one URL and a consumer has to infer
 * the relationship; `buildStaticWebPageJsonLd` already does this for the four
 * static pages.
 *
 * Nodes that render behind a Suspense boundary or a backend-gated module (the
 * FAQ on both page types) are not passed here. They emit their own script where
 * they render, carrying `isPartOf` back to this page node, so the graph still
 * connects without gating the shell on a streamed fetch or publishing markup
 * for a module that never rendered.
 */
export function buildPageGraphJsonLd({
  url,
  pageType,
  name,
  description,
  inLanguage = 'en',
  breadcrumb,
  mainEntityId,
  dateModifiedMs,
  nodes = [],
}: {
  url: string;
  pageType: 'WebPage' | 'SearchResultsPage';
  name?: string;
  description?: string;
  inLanguage?: string;
  breadcrumb?: GraphNode;
  mainEntityId?: string | Array<string>;
  /** Epoch ms of the newest listing update the page shows; never a build or request time. */
  dateModifiedMs?: number;
  nodes?: Array<GraphNode>;
}) {
  const dateModified = isoDate(dateModifiedMs);
  const breadcrumbNode = breadcrumb ? { ...asGraphNode(breadcrumb), '@id': breadcrumbNodeId(url) } : undefined;
  const mainEntityIds = mainEntityId === undefined ? [] : [mainEntityId].flat();

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': pageType,
        '@id': pageNodeId(url),
        url,
        ...(name ? { name } : {}),
        ...(description ? { description } : {}),
        inLanguage,
        ...(dateModified ? { dateModified } : {}),
        isPartOf: { '@id': WEBSITE_NODE_ID },
        ...(breadcrumbNode ? { breadcrumb: { '@id': breadcrumbNodeId(url) } } : {}),
        ...(mainEntityIds.length
          ? { mainEntity: mainEntityIds.length === 1 ? { '@id': mainEntityIds[0] } : mainEntityIds.map((id) => ({ '@id': id })) }
          : {}),
      },
      ...(breadcrumbNode ? [breadcrumbNode] : []),
      ...nodes.map(asGraphNode),
    ],
  };
}

/**
 * WebSite + Organization schema for the homepage.
 */
export function buildWebSiteJsonLd(origin: string) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: 'HotPads',
        alternateName: 'HotPads.com',
        url: `${origin}/`,
        logo: { '@type': 'ImageObject', url: `${origin}/icon.png` },
        sameAs: [
          'https://www.facebook.com/hotpads',
          'https://x.com/hotpads',
          'https://www.instagram.com/hotpads/',
          'https://www.linkedin.com/company/hotpads',
          'https://en.wikipedia.org/wiki/HotPads',
          'https://www.wikidata.org/wiki/Q5909627',
        ],
        // Same sentence as the /llms.txt summary, so the two agree on what
        // HotPads is. This is the node that answers "what is HotPads" in a
        // knowledge panel and in an AI answer.
        description:
          'HotPads is a United States rental marketplace for apartments, houses, condos, townhomes, and rooms for rent. Rentals only — there is no for-sale inventory.',
        foundingDate: '2005',
        areaServed: { '@type': 'Country', name: 'United States' },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          url: 'https://help.hotpads.com',
          areaServed: 'US',
          availableLanguage: ['English'],
        },
        parentOrganization: {
          '@type': 'Organization',
          name: 'Zillow Group',
          url: 'https://www.zillowgroup.com/',
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        name: 'HotPads',
        alternateName: 'HotPads.com',
        url: `${origin}/`,
        publisher: { '@id': `${origin}/#organization` },
        // Google retired the sitelinks searchbox, but a SearchAction template is
        // how an agent learns it may query the site directly. The shape matches
        // the {area}/{search-slug} grammar /llms.txt publishes in prose.
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${origin}/{search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };
}

/**
 * `/mobile-apps` is indexable and links to both app stores, so it publishes a
 * page node plus one MobileApplication per store listing.
 */
export function buildMobileAppsJsonLd({
  url,
  name,
  description,
  apps,
  inLanguage = 'en',
}: {
  url: string;
  name: string;
  description: string;
  apps: Array<{ name: string; operatingSystem: string; installUrl: string }>;
  inLanguage?: string;
}) {
  return buildPageGraphJsonLd({
    url,
    pageType: 'WebPage',
    name,
    description,
    inLanguage,
    mainEntityId: apps.map((app) => `${url}#${app.operatingSystem.toLowerCase()}`),
    nodes: apps.map((app) => ({
      '@type': 'MobileApplication',
      '@id': `${url}#${app.operatingSystem.toLowerCase()}`,
      name: app.name,
      operatingSystem: app.operatingSystem,
      installUrl: app.installUrl,
      applicationCategory: 'LifestyleApplication',
      publisher: { '@id': `${CANONICAL_ORIGIN}/#organization` },
    })),
  });
}

/**
 * WebPage + breadcrumb schema for indexable static pages, tied to the
 * homepage's WebSite graph node.
 */
export function buildStaticWebPageJsonLd(
  origin: string,
  path: string,
  name: string,
  description: string,
  inLanguage = 'en',
) {
  const url = `${origin}${path}`;
  const breadcrumb = buildBreadcrumbListJsonLd([], origin, { home: true, current: { label: name, url } });
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name,
        description,
        inLanguage,
        isPartOf: { '@id': `${origin}/#website` },
        ...(breadcrumb ? { breadcrumb: { '@id': `${url}#breadcrumb` } } : {}),
      },
      ...(breadcrumb ? [{ ...asGraphNode(breadcrumb), '@id': `${url}#breadcrumb` }] : []),
    ],
  };
}

type MinimalListing = {
  title?: string;
  modelSummary?: {
    minPrice?: number;
    maxPrice?: number;
    minBeds?: number;
  };
  uriMalone?: string;
  aliasEncoded?: string;
  medPhotoUrl?: string;
  medPhotoUrls?: Array<string>;
  propertyType?: string;
  opensBuildingPage?: boolean;
  has3dTour?: boolean;
  tourUrls?: Array<string>;
  tourLabels?: Array<string>;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    hideStreet?: boolean;
  };
};

/**
 * A search card's price range as an AggregateOffer. SRP cards are live search
 * results, so the availability claim is accurate here; the expired-listing case
 * only arises on an HDP (HP-9025).
 */
function cardAggregateOffer(modelSummary: MinimalListing['modelSummary'], url: string | undefined) {
  const low = validPrice(modelSummary?.minPrice);
  if (low === undefined) return undefined;
  return {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: low,
    highPrice: Math.max(validPrice(modelSummary?.maxPrice) ?? low, low),
    availability: 'https://schema.org/InStock',
    ...(url ? { url } : {}),
  };
}

/** The one label every page publishes and shows for the site root. */
export const HOME_BREADCRUMB_LABEL = 'Home';

type BreadcrumbListOptions = {
  /** Prepend a Home item. The backend trail on SRPs and listing pages starts at the state. */
  home?: boolean;
  /** Append the current page as the last item. Listing pages: the backend trail stops at the area. */
  current?: { label: string; url: string };
};

/**
 * Returns undefined when fewer than two labelled items survive, whatever the
 * options. An absent BreadcrumbList is valid; one with an empty itemListElement
 * is an error, and GSC has been reporting ~8,882 of them since 2019 (HP-9034)
 * from building pages and area-scoped near-me pages whose backend trail comes
 * back empty. A single item is no fix either: Google asks for at least two, so a
 * Home item alone, or a current page alone, also emits nothing.
 *
 * `position` is assigned last, over the final list, so it is contiguous from 1
 * however many items `home` and `current` add. A trail that already starts at
 * Home, or already ends at the current page, is not given a second copy.
 */
export function buildBreadcrumbListJsonLd(
  items: Array<Breadcrumb> | undefined,
  origin: string,
  { home, current }: BreadcrumbListOptions = {},
) {
  const absolute = (url: string) => (url.startsWith('http') ? url : `${origin}${url}`);
  const trail = (items ?? [])
    .filter((entry) => entry.label)
    .map((entry) => {
      const url = entry.url ?? entry.href;
      return { name: entry.label, ...(url ? { item: absolute(url) } : {}) };
    });

  const homeUrl = `${origin}/`;
  const currentUrl = current && absolute(current.url);
  const entries = [
    ...(home && trail[0]?.item !== homeUrl ? [{ name: HOME_BREADCRUMB_LABEL, item: homeUrl }] : []),
    ...trail,
    ...(current?.label && trail.at(-1)?.item !== currentUrl ? [{ name: current.label, item: currentUrl }] : []),
  ];
  if (entries.length < 2) return undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: entries.map((entry, index) => ({ '@type': 'ListItem', position: index + 1, ...entry })),
  };
}

/**
 * Each ListItem nests a typed Accommodation-family entity under `item` (rather
 * than flattening name/address onto the ListItem, which doesn't define them).
 * The entity `@id` comes from the same `listingCanonicalUrl` + `listingNodeId`
 * pair the HDP uses, so both pages describe the same thing even when the path
 * differs in case or percent-encoding.
 *
 * Each item also carries the price and bed count the visible card already
 * shows (HP-9027), so a search page answers "2-bedroom apartments in Austin
 * under $2,000" without a consumer crawling forty listing pages.
 *
 * Toured listings carry 3DModels via `subjectOf` — with `contentUrl` when the
 * V3 card provides tour urls (HP-8526); listings that only signal a tour exists
 * keep the bare node (HP-8483).
 */
export function buildItemListJsonLd(listings: Array<MinimalListing>, listName: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: listings.length,
    itemListElement: listings.map((listing, index) => {
      const hdpUrl = listing.uriMalone || (listing.aliasEncoded ? `/listings/${listing.aliasEncoded}` : undefined);
      const url = hdpUrl ? listingCanonicalUrl(hdpUrl) : undefined;
      const photo = listing.medPhotoUrl || listing.medPhotoUrls?.[0];
      const address = listingPostalAddress(listing.address);
      const tourModels = tour3dModelNodes(listing.tourUrls ?? [], listing.tourLabels ?? []);
      const offers = cardAggregateOffer(listing.modelSummary, url);
      const numberOfBedrooms = listing.modelSummary?.minBeds;
      const subjectOf = tourModels.length
        ? tourModels
        : listing.has3dTour
          ? [{ '@type': '3DModel', name: '3D tour' }]
          : [];
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': listingSchemaType(listing, Boolean(listing.opensBuildingPage)),
          ...(url ? { '@id': listingNodeId(url), url } : {}),
          name: publicListingName(listing) ?? 'Rental listing',
          ...(photo ? { image: photo } : {}),
          ...(address ? { address } : {}),
          ...(typeof numberOfBedrooms === 'number' ? { numberOfBedrooms } : {}),
          ...(offers ? { offers } : {}),
          ...(subjectOf.length ? { subjectOf } : {}),
        },
      };
    }),
  };
}
